import { useState, useEffect, useMemo } from 'react';

/**
 * Custom hook to detect if the app is running inside an iframe
 * In dev mode, can be overridden using localStorage:
 * - localStorage.setItem('dev_force_iframe', 'true') - Force iframe mode
 * - localStorage.setItem('dev_force_iframe', 'false') - Force non-iframe mode
 * - localStorage.removeItem('dev_force_iframe') - Use actual detection
 * @returns {boolean} - true if running in iframe, false otherwise
 */
const useIsInIframe = () => {
  // Check iframe status synchronously to avoid flash
  const checkIfInIframe = () => {
    try {
      // Check if window.self is not equal to window.top
      const result = window.self !== window.top;
      console.log('🔍 Iframe check:', {
        'window.self': window.self,
        'window.top': window.top,
        'result': result
      });
      return result;
    } catch (e) {
      // If we get a security error accessing window.top (cross-origin),
      // we're definitely in an iframe
      console.log('🔍 Iframe check (caught error):', e.message, '- assuming iframe');
      return true;
    }
  };

  // Get initial value synchronously
  const getInitialValue = () => {
    // First check actual iframe status
    const actualIframeStatus = checkIfInIframe();

    // In dev mode, allow override ONLY if not in actual iframe
    if (process.env.NODE_ENV === 'development' && !actualIframeStatus) {
      const devOverride = localStorage.getItem('dev_force_iframe');
      console.log('🔍 Dev mode override:', devOverride);
      if (devOverride !== null) {
        const overrideValue = devOverride === 'true';
        console.log('🔍 Using dev override:', overrideValue);
        return overrideValue;
      }
    }

    // Use actual iframe status
    console.log('🔍 Final iframe status:', actualIframeStatus);
    return actualIframeStatus;
  };

  // Use lazy initialization to avoid flash
  const [isInIframe, setIsInIframe] = useState(() => getInitialValue());

  useEffect(() => {
    // Double-check on mount and update if needed
    const currentValue = getInitialValue();
    if (currentValue !== isInIframe) {
      setIsInIframe(currentValue);
    }
  }, [isInIframe]);

  return isInIframe;
};

export default useIsInIframe;
